/**
 * ÜST BAR — TailAdmin AppHeader düzeni: sidebar daralt/genişlet düğmesi, ⌘K arama,
 * light/dark tema düğmesi, bildirim zili (noktalı), kullanıcı menüsü. Geolop'a özel:
 * sayfa adı + aktif site rozeti (tık → Dashboard).
 */
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { logout } from "@/lib/auth";
import { useMe } from "@/hooks/use-me";
import { shellStore, useShell } from "@/lib/ui-shell-store";
import { Search, Moon, Sun, Bell, Menu, X, ChevronDown, UserRound, Crosshair, LogOut, ShieldAlert, AlertTriangle, Info } from "lucide-react";
import { SiteFavicon } from "@/components/site-favicon";

/**
 * NÖBETÇİ ZİLİ — sahte "Planlama kısayolu" zilin yerine GERÇEK bildirim merkezi:
 * kullanıcının tüm sitelerindeki okunmamış Nöbetçi olayları. Kırmızı nokta yalnız
 * okunmamış varken yanar; açılır listede son olaylar, alt satırda Nöbetçi ekranına geçiş.
 */
function NobetciZil() {
  const [acik, setAcik] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { data, refetch } = useQuery({
    queryKey: ["nobetci-zil"],
    refetchInterval: 60_000,
    queryFn: async () => {
      const r = await fetch("/api/nobetci/zil");
      return r.ok ? (r.json() as Promise<{ okunmamis: number; olaylar: { id: number; tur: string; onem: string; mesaj: string; site: string; okundu: boolean; createdAt: string }[] }>) : null;
    },
  });
  useEffect(() => {
    const kapat = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setAcik(false); };
    document.addEventListener("mousedown", kapat);
    return () => document.removeEventListener("mousedown", kapat);
  }, []);
  const okunduSay = async () => {
    await fetch("/api/nobetci/zil/okundu", { method: "POST" });
    void refetch();
  };
  const zamanKisa = (iso: string) => {
    const fark = Date.now() - new Date(iso).getTime();
    if (fark < 3600_000) return `${Math.max(1, Math.round(fark / 60_000))}dk`;
    if (fark < 86_400_000) return `${Math.round(fark / 3600_000)}sa`;
    return `${Math.round(fark / 86_400_000)}g`;
  };
  const n = data?.okunmamis ?? 0;
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setAcik((v) => !v)} title={n > 0 ? `${n} okunmamış Nöbetçi bildirimi` : "Nöbetçi bildirimleri"}
        className="relative flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 dark:border-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03]">
        <Bell className="h-5 w-5" />
        {n > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">{n > 99 ? "99+" : n}</span>
        )}
      </button>
      {acik && (
        <div className="absolute right-0 top-12 z-50 w-96 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-theme-lg dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5 dark:border-gray-800">
            <span className="flex items-center gap-1.5 text-theme-sm font-semibold text-gray-800 dark:text-white/90"><ShieldAlert className="h-4 w-4 text-brand-500" /> Nöbetçi</span>
            {n > 0 && <button onClick={() => void okunduSay()} className="text-[11px] font-medium text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">tümünü okundu say</button>}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {!data || data.olaylar.length === 0 ? (
              <div className="px-4 py-8 text-center text-theme-sm text-gray-400">Her şey yolunda — son 14 günde olay yok.</div>
            ) : data.olaylar.map((o) => (
              <div key={o.id} className={`border-b border-gray-50 px-4 py-2.5 text-[12.5px] leading-snug dark:border-gray-800/60 ${o.okundu ? "opacity-60" : ""}`}>
                <div className="flex items-start gap-2">
                  {o.onem === "kritik" ? <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" /> : <Info className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${o.onem === "uyari" ? "text-amber-500" : "text-blue-400"}`} />}
                  <div className="min-w-0">
                    <div className="text-gray-700 dark:text-gray-200">{o.mesaj}</div>
                    <div className="mt-0.5 text-[10.5px] text-gray-400">{o.site} · {zamanKisa(o.createdAt)} önce</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Link href="/nobetci">
            <span onClick={() => setAcik(false)} className="block cursor-pointer bg-gray-50 px-4 py-2.5 text-center text-[12px] font-semibold text-brand-600 hover:bg-gray-100 dark:bg-white/[0.03] dark:hover:bg-white/[0.06]">
              Nöbetçi ekranını aç →
            </span>
          </Link>
        </div>
      )}
    </div>
  );
}

/**
 * CANLI TOKEN HALKASI — 30 günlük kullanım / paket hakkı, dakikada bir tazelenir
 * (asistan/ajan işlemlerinden sonra da sorgu tazeleme ile güncellenir). Paketsiz hesapta
 * gri halka + "sınırsız" ipucu. Tıklayınca Hesabım'daki tam döküme gider.
 */
const TOKENS_PER_UNIT = 1000;
function TokenRing() {
  const { data } = useQuery({
    queryKey: ["me-usage-ring"],
    refetchInterval: 60_000,
    staleTime: 30_000,
    queryFn: async () => {
      const r = await fetch("/api/me/usage");
      return r.ok ? (r.json() as Promise<{ package: { name: string; tokenBudgetUsd: number } | null; usage30: number }>) : null;
    },
  });
  if (!data) return null;
  const used = Math.round((data.usage30 ?? 0) * TOKENS_PER_UNIT);
  const cap = data.package ? Math.round(data.package.tokenBudgetUsd * TOKENS_PER_UNIT) : null;
  const pct = cap ? Math.min(100, (used / cap) * 100) : 0;
  const color = !cap ? "#a3a3a0" : pct >= 90 ? "#d92d20" : pct >= 70 ? "#d97706" : "#12b76a";
  const R = 8.5;
  const C = 2 * Math.PI * R;
  const title = cap
    ? `Token (30 gün): ${used.toLocaleString("tr-TR")} / ${cap.toLocaleString("tr-TR")} — %${Math.round(pct)} kullanıldı${pct >= 90 ? " ⚠️" : ""}`
    : `Token (30 gün): ${used.toLocaleString("tr-TR")} — paketsiz, sınırsız`;
  return (
    <Link href="/account">
      <button title={title} className="flex h-10 items-center gap-1.5 rounded-lg border border-gray-200 px-2 text-gray-500 hover:bg-gray-50 dark:border-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03]">
        <svg width="22" height="22" viewBox="0 0 22 22" className="shrink-0">
          <circle cx="11" cy="11" r={R} fill="none" strokeWidth="3" className="stroke-gray-200 dark:stroke-gray-700" />
          <circle cx="11" cy="11" r={R} fill="none" strokeWidth="3" stroke={color} strokeLinecap="round"
            strokeDasharray={`${(Math.max(cap ? pct : 100, 2) / 100) * C} ${C}`} transform="rotate(-90 11 11)" />
        </svg>
        <span className="hidden xl:inline text-[11px] font-semibold" style={{ color }}>
          {cap ? `%${Math.round(pct)}` : "∞"}
        </span>
      </button>
    </Link>
  );
}

const TITLES: { match: (p: string) => boolean; title: string }[] = [
  { match: (p) => p === "/", title: "Genel Bakış" },
  { match: (p) => p.startsWith("/kokpit"), title: "Genel Bakış" },
  { match: (p) => p.startsWith("/firsatlar"), title: "Fırsatlar Merkezi" },
  { match: (p) => p.startsWith("/icerik-gunlugu"), title: "İçerik Günlüğü" },
  { match: (p) => p.startsWith("/planning") || p.startsWith("/action-plan"), title: "Planlama" },
  { match: (p) => p.includes("/clusters"), title: "Cluster Maps" },
  { match: (p) => /^\/domains\/\d+$/.test(p), title: "Link İnşası" },
  { match: (p) => p.startsWith("/domains/new"), title: "Yeni Tarama" },
  { match: (p) => p.startsWith("/pages/"), title: "Sayfa Detayı" },
  { match: (p) => p.startsWith("/assistant"), title: "Geolop Asistanı" },
  { match: (p) => p.startsWith("/content-studio"), title: "İçerik Stüdyosu" },
  { match: (p) => p.startsWith("/content-calendar"), title: "İçerik Takvimi" },
  { match: (p) => p.startsWith("/content-opportunities"), title: "İçerik Üretim Ajanı" },
  { match: (p) => p.startsWith("/content-detail"), title: "İçerik Detayı" },
  { match: (p) => p.startsWith("/content-planner") || p.startsWith("/planner-"), title: "Brief Keşif Ajanı" },
  { match: (p) => p.startsWith("/content-translate"), title: "Çeviri Ajanı" },
  { match: (p) => p.startsWith("/page-strengthen"), title: "Sayfa Güçlendirme" },
  { match: (p) => p.startsWith("/backlink-audit"), title: "Backlink Ajanı" },
  { match: (p) => p.startsWith("/tech-audit"), title: "Teknik SEO Ajanı" },
  { match: (p) => p.startsWith("/rank-tracking"), title: "Sıralama Takip Ajanı" },
  { match: (p) => p.startsWith("/index-radar"), title: "Dizin Radarı" },
  { match: (p) => p.startsWith("/keyword-research"), title: "Anahtar Kelime Araştırma" },
  { match: (p) => p.startsWith("/seo-report"), title: "SEO-GEO Raporu" },
  { match: (p) => p.startsWith("/bot-log"), title: "AI Bot Analizi" },
  { match: (p) => p.startsWith("/traffic-analysis"), title: "Trafik Analizi" },
  { match: (p) => p.startsWith("/quick-analysis"), title: "Hızlı Analiz" },
  { match: (p) => p.startsWith("/account"), title: "Hesabım" },
  { match: (p) => p.startsWith("/admin"), title: "Admin Paneli" },
  { match: (p) => p.startsWith("/settings"), title: "Ayarlar" },
];

export function TopBar() {
  const [location, setLocation] = useLocation();
  const { data: domains } = useListDomains();
  const domainIdMatch = location.match(/\/domains\/(\d+)/);
  const [activeDomainId] = useActiveDomain(domains, domainIdMatch ? parseInt(domainIdMatch[1]) : null);
  const active = (domains ?? []).find((d) => d.id === activeDomainId) ?? null;
  const { data: user } = useMe();
  const [avatarHata, setAvatarHata] = useState(false);
  const title = TITLES.find((t) => t.match(location))?.title ?? "Geolop";
  const { theme, mobileOpen } = useShell();
  const [userOpen, setUserOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  // ⌘K / Ctrl+K → arama kutusuna odak (TailAdmin davranışı)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); inputRef.current?.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Kullanıcı menüsü: dışına tıklayınca kapanır
  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (userOpen && userRef.current && !userRef.current.contains(e.target as Node)) setUserOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [userOpen]);

  const iconBtn = "flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition hover:bg-gray-50 hover:text-gray-700 dark:border-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-white/90";

  return (
    <header className="sticky top-0 z-30 flex w-full items-center gap-3 border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-800 dark:bg-gray-900 md:px-6">
      {/* Sidebar daralt/genişlet (mobilde çekmece) */}
      <button
        onClick={() => (window.innerWidth >= 1024 ? shellStore.toggleSidebar() : shellStore.toggleMobile())}
        className={iconBtn}
        aria-label="Menüyü aç/kapat"
      >
        {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      <h2 className="shrink-0 text-lg font-semibold text-gray-800 dark:text-white/90">{title}</h2>

      {/* Aktif site rozeti → DASHBOARD */}
      {active && (
        <Link href="/">
          <span className="hidden cursor-pointer items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1 text-theme-xs font-medium text-gray-600 hover:bg-gray-100 dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-300 dark:hover:bg-white/[0.06] md:inline-flex">
            <SiteFavicon url={active.url} size={14} />
            {active.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
          </span>
        </Link>
      )}

      <div className="flex-1" />

      {/* Arama (⌘K) */}
      <div className="relative hidden lg:block">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          ref={inputRef}
          placeholder="Sayfa ara…"
          onKeyDown={(e) => {
            if (e.key === "Enter" && activeDomainId) {
              const q = (e.target as HTMLInputElement).value.trim();
              setLocation(`/domains/${activeDomainId}${q ? `?q=${encodeURIComponent(q)}` : ""}`);
            }
          }}
          className="h-10 w-64 rounded-lg border border-gray-200 bg-gray-50 pl-9 pr-14 text-theme-sm text-gray-800 outline-none placeholder:text-gray-400 focus:border-brand-300 focus:shadow-focus-ring dark:border-gray-800 dark:bg-white/[0.03] dark:text-white/90"
        />
        <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] text-gray-400 dark:border-gray-700 dark:bg-gray-800">⌘K</span>
      </div>

      {/* Canlı token halkası (Claude usage tarzı) — tık → Hesabım */}
      <TokenRing />

      {/* Tema */}
      <button onClick={() => shellStore.toggleTheme()} className={iconBtn} title={theme === "dark" ? "Açık temaya geç" : "Koyu temaya geç"}>
        {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      </button>

      {/* Nöbetçi bildirim zili (gerçek bildirimler — kesinti, değişiklik, dizin olayları) */}
      <NobetciZil />

      {/* Kullanıcı menüsü */}
      <div className="relative" ref={userRef}>
        <button onClick={() => setUserOpen((v) => !v)} className="flex h-10 items-center gap-2 rounded-lg border border-gray-200 px-2.5 text-theme-sm text-gray-700 hover:bg-gray-50 dark:border-gray-800 dark:text-gray-300 dark:hover:bg-white/[0.03]">
          {user?.avatarUrl && !avatarHata ? (
            <img src={user.avatarUrl} alt="" referrerPolicy="no-referrer" onError={() => setAvatarHata(true)}
              className="h-7 w-7 rounded-full object-cover" />
          ) : (
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-500 text-[11px] font-bold text-white">
              {(user?.name?.trim() || user?.email || "?").slice(0, 1).toUpperCase()}
            </span>
          )}
          <span className="hidden max-w-28 truncate xl:inline">{user?.name?.trim() || user?.email}</span>
          <ChevronDown className={`h-4 w-4 text-gray-400 transition ${userOpen ? "rotate-180" : ""}`} />
        </button>
        {userOpen && (
          <div className="absolute right-0 top-12 z-50 w-56 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-theme-lg dark:border-gray-800 dark:bg-gray-900">
            <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-800">
              <p className="truncate text-theme-sm font-medium text-gray-800 dark:text-white/90">{user?.name?.trim() || "Kullanıcı"}</p>
              <p className="truncate text-theme-xs text-gray-400">{user?.email}</p>
              {user?.role && <p className="truncate text-[10px] font-semibold tracking-wide text-gray-400 dark:text-gray-500">{user.role}</p>}
            </div>
            <Link href="/account">
              <span onClick={() => setUserOpen(false)} className="flex cursor-pointer items-center gap-2.5 px-4 py-2.5 text-theme-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/[0.03]">
                <UserRound className="h-4 w-4 text-gray-400" /> Hesabım
              </span>
            </Link>
            <Link href="/planning">
              <span onClick={() => setUserOpen(false)} className="flex cursor-pointer items-center gap-2.5 px-4 py-2.5 text-theme-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/[0.03]">
                <Crosshair className="h-4 w-4 text-gray-400" /> Planlama
              </span>
            </Link>
            <button onClick={() => logout()} className="flex w-full items-center gap-2.5 border-t border-gray-100 px-4 py-2.5 text-theme-sm text-error-500 hover:bg-error-50 dark:border-gray-800 dark:hover:bg-error-500/10">
              <LogOut className="h-4 w-4" /> Çıkış Yap
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
